// Prospect-local time helpers. Pure (Intl only), unit-tested in tests/fd-dialer.test.ts.
// Every callback time the setter picks is expressed in the PROSPECT's time zone (businesses.tz), then stored as UTC.

export interface ZonedParts {
  year: number;
  month: number;   // 1-12
  day: number;
  hour: number;    // 0-23
  minute: number;
  weekday: number; // 0 = Sunday ... 6 = Saturday
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const partsFmtCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', weekday: 'short',
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
    partsFmtCache.set(tz, f);
  }
  return f;
}

/** Wall-clock parts of `date` in `tz`. Throws RangeError for an invalid IANA zone. */
export function zonedParts(date: Date, tz: string): ZonedParts {
  const out: Record<string, string> = {};
  for (const p of partsFormatter(tz).formatToParts(date)) out[p.type] = p.value;
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    weekday: WEEKDAYS[out.weekday] ?? 0,
  };
}

/** Offset of `tz` from UTC at instant `ms`, in ms (e.g. -4 h for Toronto in summer). */
function offsetAt(ms: number, tz: string): number {
  const p = zonedParts(new Date(ms), tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  return asUtc - Math.floor(ms / 60000) * 60000;
}

/**
 * The UTC instant at which the wall clock in `tz` shows the given local date and time.
 * Spring-forward gap (a local time that doesn't exist): the result lands one hour later (2:30 → 3:30).
 * Fall-back overlap (a local time that happens twice): the first occurrence (daylight time).
 */
export function zonedToUtc(local: { year: number; month: number; day: number; hour: number; minute: number }, tz: string): Date {
  const guess = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
  const o1 = offsetAt(guess, tz);
  const ms1 = guess - o1;
  const o2 = offsetAt(ms1, tz);
  if (o1 === o2) return new Date(ms1);
  const ms2 = guess - o2;
  if (offsetAt(ms2, tz) === o2) return new Date(ms2);
  return new Date(ms1); // in the gap
}

/** Adds whole calendar days to a local date (month/year roll-over handled by Date.UTC). */
export function addLocalDays(p: { year: number; month: number; day: number }, days: number): { year: number; month: number; day: number; weekday: number } {
  const d = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), weekday: d.getUTCDay() };
}

export function fmtTime(date: Date, tz: string): string {
  return date.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
}

export function fmtDayTime(date: Date, tz: string, withZone = true): string {
  return date.toLocaleString('en-US', {
    timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZoneName: withZone ? 'short' : undefined,
  });
}

/** 'EDT', 'CST', 'PDT', or a GMT offset when the runtime has no abbreviation. */
export function tzAbbrev(date: Date, tz: string): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date).find((p) => p.type === 'timeZoneName');
  return part?.value ?? tz;
}

/** 'Tue 10:14 AM' in the prospect's zone. */
export function localClock(date: Date, tz: string): string {
  return date.toLocaleString('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

/** Local-time bucket used by the queue: early < 11:00 <= mid < 15:00 <= late. */
export function localBucket(hour: number): 'early' | 'mid' | 'late' {
  if (hour < 11) return 'early';
  if (hour < 15) return 'mid';
  return 'late';
}

/** 'YYYY-MM-DD' of `date` in `tz`. */
export function localDateKey(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** `<input type="datetime-local">` value ('YYYY-MM-DDTHH:MM') for `date` shown in `tz`. */
export function toDatetimeLocal(date: Date, tz: string): string {
  const p = zonedParts(date, tz);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Parses a datetime-local value as wall-clock time in `tz`. Returns null when malformed. */
export function fromDatetimeLocal(value: string, tz: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [year, month, day, hour, minute] = m.slice(1).map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  return zonedToUtc({ year, month, day, hour, minute }, tz);
}

export interface CallbackPreset {
  key: 'in30' | 'in60' | 'earliest' | 'in4h' | 'tomorrow9' | 'tomorrow14' | 'nextTue10';
  label: string;   // shown on the button, prospect-local
  at: Date;
}

/** The dial gate's 4 h between two attempts to one number (SPEC §5.1 `attempt_spacing`): the "In 4 h" preset. Since PJ
 *  2026-10-21 it no longer limits a callback time (In 30 min and In 1 hour come first). */
export const CALLBACK_MIN_GAP_MS = 4 * 3600_000;

/** PJ 2026-10-21 ("we can set a callback for any time. Even if it's just in 30 minutes or an hour"): the quick presets,
 *  first in the row. */
export const CALLBACK_QUICK_PRESETS: ReadonlyArray<{ key: 'in30' | 'in60'; minutes: number; word: string }> = [
  { key: 'in30', minutes: 30, word: 'In 30 min' },
  { key: 'in60', minutes: 60, word: 'In 1 hour' },
];

/** Next Monday-Friday date strictly after `from` (local). */
function nextWeekday(from: ZonedParts): { year: number; month: number; day: number; weekday: number } {
  for (let i = 1; i <= 7; i++) {
    const d = addLocalDays(from, i);
    if (d.weekday >= 1 && d.weekday <= 5) return d;
  }
  return addLocalDays(from, 1);
}

/**
 * The callback presets from SPEC §15.1, in the prospect's zone: in 30 min · in 1 hour · later today · tomorrow 9:00 ·
 * tomorrow 14:00 · next Tue 10:00.
 *
 * "In 30 min" and "In 1 hour" (PJ 2026-10-21): a callback may be set for any time from now on, because the dial gate
 * waives the 4 h spacing and the other soft limits for a callback they asked for once it is due. Each is now + 30 / 60
 * minutes rounded up to the whole minute, and left out when it lands on another day or outside the default calling hours
 * (as "In 4 h"); the date-time box takes any other time, and only the server refuses one (calling hours, or a 4th call
 * to the number in 24 hours).
 * "Later today" is then:
 * - `earliestAt` when given (the server's `details.earliestAt` from a 400 callback_not_allowed) and still ahead:
 *   "Earliest allowed: Tue 3:00 PM". The server computed it with the real windows, holidays and the 3-a-day cap.
 * - otherwise "In 4 h", rounded up to 5 min, and left out when that lands outside the default calling hours (the
 *   "tomorrow" presets cover it).
 * "Tomorrow" skips the weekend (a 9:00 Saturday call is outside the Saturday window and Sunday is never dialled);
 * the label always names the real day so the setter can say it out loud.
 */
export function callbackPresets(now: Date, tz: string, opts: { earliestAt?: string | null } = {}): CallbackPreset[] {
  const p = zonedParts(now, tz);
  const tomorrow = nextWeekday(p);
  const isTomorrow = (() => { const t = addLocalDays(p, 1); return t.day === tomorrow.day && t.month === tomorrow.month; })();
  const dayWord = isTomorrow ? 'Tomorrow' : new Date(Date.UTC(tomorrow.year, tomorrow.month - 1, tomorrow.day)).toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  let tueOffset = (2 - p.weekday + 7) % 7;
  if (tueOffset === 0) tueOffset = 7;
  const tue = addLocalDays(p, tueOffset);
  const at = (d: { year: number; month: number; day: number }, hour: number) => zonedToUtc({ ...d, hour, minute: 0 }, tz);

  const out: CallbackPreset[] = [];
  for (const q of CALLBACK_QUICK_PRESETS) {
    const soon = new Date(Math.ceil((now.getTime() + q.minutes * 60_000) / 60_000) * 60_000); // rounded up to the minute
    // Review 2026-10-21: a call must start 2 minutes before calling hours close (the server's last start), so 7:59 PM is
    // never offered; the server would refuse it and point to tomorrow though 7:58 PM still works.
    if (localDateKey(soon, tz) === localDateKey(now, tz) && startsInsideDefaultHours(soon, tz)) {
      out.push({ key: q.key, label: `${q.word} (${fmtTime(soon, tz)})`, at: soon });
    }
  }
  const earliest = opts.earliestAt ? new Date(opts.earliestAt) : null;
  if (earliest && !Number.isNaN(earliest.getTime()) && earliest.getTime() > now.getTime()) {
    const sameDay = localDateKey(earliest, tz) === localDateKey(now, tz);
    const when = sameDay ? fmtTime(earliest, tz) : fmtDayTime(earliest, tz, false);
    out.push({ key: 'earliest', label: `Earliest allowed: ${when}`, at: earliest });
  } else {
    const in4h = new Date(Math.ceil((now.getTime() + CALLBACK_MIN_GAP_MS) / 300_000) * 300_000); // rounded up to 5 min
    const sameDay = localDateKey(in4h, tz) === localDateKey(now, tz);
    if (sameDay && !outsideDefaultHours(in4h, tz)) out.push({ key: 'in4h', label: `In 4 h (${fmtTime(in4h, tz)})`, at: in4h });
  }
  out.push(
    { key: 'tomorrow9', label: `${dayWord} 9:00 AM`, at: at(tomorrow, 9) },
    { key: 'tomorrow14', label: `${dayWord} 2:00 PM`, at: at(tomorrow, 14) },
    { key: 'nextTue10', label: `${tueOffset === 1 ? 'Tomorrow (Tue)' : 'Next Tue'} 10:00 AM`, at: at(tue, 10) },
  );
  return out;
}

/**
 * The spec's default calling hours (Mon-Fri 09:00-20:00, Sat 10:00-17:00, Sunday only where set: SPEC §19). Only used for a soft warning on a
 * hand-picked callback time; the server's dial gate (which knows state overrides and holidays) is the real rule.
 */
export function outsideDefaultHours(date: Date, tz: string): string | null {
  const p = zonedParts(date, tz);
  const mins = p.hour * 60 + p.minute;
  if (p.weekday === 0) return 'That is a Sunday; Sunday calls are US only, shorter hours, and not in every state. The server will check.';
  if (p.weekday === 6) return mins < 600 || mins >= 17 * 60 ? 'Saturday calling hours are 10:00 AM to 5:00 PM their time.' : null;
  return mins < 540 || mins >= 20 * 60 ? 'Weekday calling hours are 9:00 AM to 8:00 PM their time.' : null;
}

/** The server's margin before calling hours close: a call must start at least this long before (gates.ts
 *  WINDOW_CLOSE_MARGIN_MS, SPEC §5.1 outside_window). */
export const WINDOW_CLOSE_MARGIN_MS = 2 * 60_000;

/** True when a call may start at `date` in the default calling hours: inside them, and at least WINDOW_CLOSE_MARGIN_MS
 *  before they close (review 2026-10-21: the quick callback presets used to offer 7:59 PM, which the server refuses). */
export function startsInsideDefaultHours(date: Date, tz: string): boolean {
  return !outsideDefaultHours(date, tz) && !outsideDefaultHours(new Date(date.getTime() + WINDOW_CLOSE_MARGIN_MS - 1), tz);
}

/** Validates a callback time for dm_later / callback_set: future, within 60 days (SPEC §8.3). No minimum gap: a
 *  callback 5 minutes out is fine (PJ 2026-10-21); the server alone refuses calling hours and the 3-a-day cap. */
export function callbackProblem(date: Date | null, now: Date): string | null {
  if (!date || Number.isNaN(date.getTime())) return 'Pick a callback time.';
  if (date.getTime() <= now.getTime()) return 'The callback time is in the past.';
  if (date.getTime() > now.getTime() + 60 * 86400_000) return 'Callbacks must be within 60 days.';
  return null;
}

/** '4:07' / '1:02:09' from seconds. */
export function fmtDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
